import type {
  PlanRevisionTrackerCreation,
  PlanRevisionTrackerDependency,
  PlanRevisionTrackerTelemetry,
  PlanRevisionTrackerTransition,
} from "../types";

import { isRecord, readOptionalString, toStringArray } from "./utils";

function sanitizeTransition(
  value: unknown,
): PlanRevisionTrackerTransition | null {
  if (!isRecord(value)) {
    return null;
  }
  const record = value;
  const transition: PlanRevisionTrackerTransition = {
    changed: Boolean(record.changed),
    desiredState: readOptionalString(record.desiredState),
    issueKey: readOptionalString(record.issueKey),
    previousState: readOptionalString(record.previousState),
  };
  if (typeof record.error === "string") {
    transition.error = record.error;
  }
  return transition;
}

function sanitizeCreation(value: unknown): PlanRevisionTrackerCreation | null {
  if (!isRecord(value)) {
    return null;
  }
  const record = value;
  if (
    typeof record.placeholderId !== "string" ||
    record.placeholderId.length === 0
  ) {
    return null;
  }
  const { status } = record;
  if (status !== "created" && status !== "skipped" && status !== "failed") {
    return null;
  }
  const creation: PlanRevisionTrackerCreation = {
    issueId: readOptionalString(record.issueId),
    issueKey: readOptionalString(record.issueKey),
    placeholderId: record.placeholderId,
    status,
  };
  if (typeof record.error === "string") {
    creation.error = record.error;
  }
  return creation;
}

function sanitizeDependency(
  value: unknown,
): PlanRevisionTrackerDependency | null {
  if (!isRecord(value)) {
    return null;
  }
  const record = value;
  const dependency: PlanRevisionTrackerDependency = {
    attempted: toStringArray(record.attempted),
    errors: toStringArray(record.errors),
    issueKey: readOptionalString(record.issueKey),
    linked: toStringArray(record.linked),
    removed: toStringArray(record.removed),
    skipped: toStringArray(record.skipped),
    supported: typeof record.supported === "boolean" ? record.supported : false,
  };
  return dependency;
}

export function sanitizeTracker(
  value: unknown,
): PlanRevisionTrackerTelemetry | null {
  if (!isRecord(value)) {
    return null;
  }
  const record = value;
  const { status } = record;
  if (status !== "applied" && status !== "skipped") {
    return null;
  }
  const tracker: PlanRevisionTrackerTelemetry = {
    created: Array.isArray(record.created)
      ? (record.created as unknown[])
          .map((entry: unknown) => sanitizeCreation(entry))
          .filter((entry): entry is PlanRevisionTrackerCreation =>
            Boolean(entry),
          )
      : [],
    dependencies: Array.isArray(record.dependencies)
      ? (record.dependencies as unknown[])
          .map((entry: unknown) => sanitizeDependency(entry))
          .filter((entry): entry is PlanRevisionTrackerDependency =>
            Boolean(entry),
          )
      : [],
    status,
    transitions: Array.isArray(record.transitions)
      ? (record.transitions as unknown[])
          .map((entry: unknown) => sanitizeTransition(entry))
          .filter((entry): entry is PlanRevisionTrackerTransition =>
            Boolean(entry),
          )
      : [],
  };
  if (typeof record.reason === "string") {
    tracker.reason = record.reason;
  }
  return tracker;
}
